import React, { useCallback } from "react"

import { RTF } from "@tenforce/product-fields"

import { GlobalState } from "../../../../store"
import { useAppDispatch, useAppSelector } from "../../../../hooks"
import { IRichTextField } from "../.."
import { setCurrentFieldInEditMode, updateField } from "../../slice"
import HTMLAndInternalLinkParser from "../common/HTMLAndInternalLinkParser"
import { Labels } from "../../../../util/Translations"
import env from "../../../../env"
import { getImageConnectorUrl } from "../../../../util/UrlGenerator"

export interface OwnProps {
    field: IRichTextField
}

type Props = OwnProps

const CKEDITOR_PROD_KEY =
    "eyJhbGciOiJFUzI1NiJ9.eyJleHAiOjE4MDY2MjM5OTksImp0aSI6IjQxYWIwODQ1LTk2NjgtNGFmYi05OWMzLWRjZDY3MTJhYTkyZCIsImRpc3RyaWJ1dGlvbkNoYW5uZWwiOlsic2giLCJkcnVwYWwiXSwid2hpdGVMYWJlbCI6dHJ1ZSwiZmVhdHVyZXMiOlsiRFJVUCIsIkRPIiwiRlAiLCJTQyIsIlRPQyIsIlRQTCIsIlBPRSIsIkNDIiwiTUYiLCJTRUUiLCJFQ0giLCJFSVMiLCJMSCIsIkZPTyJdLCJ2YyI6IjNlMTI4MWZiIn0.XHi9edcC9kg5MYdESbWya6YrqNctIYH-nE1Uk1r8cvDNltPd6Pa3AHzExq9IIK2hSkVdyFnRMnikyWKPBxbpog"
const CKEDITOR_DEV_KEY =
    "eyJhbGciOiJFUzI1NiJ9.eyJleHAiOjE4MDY2MjM5OTksImp0aSI6ImE2NDZmNzc5LWUxNzktNDFhNS1iMGY2LWExZGI5OWZlODNhZSIsImRpc3RyaWJ1dGlvbkNoYW5uZWwiOlsic2giLCJkcnVwYWwiXSwid2hpdGVMYWJlbCI6dHJ1ZSwibGljZW5zZVR5cGUiOiJkZXZlbG9wbWVudCIsImZlYXR1cmVzIjpbIkRSVVAiLCJETyIsIkZQIiwiU0MiLCJUT0MiLCJUUEwiLCJQT0UiLCJDQyIsIk1GIiwiU0VFIiwiRUNIIiwiRUlTIiwiTEgiLCJGT08iXSwidmMiOiI4M2NlOWQ0NSJ9.KAHOtYZ_KZj1KshhQJLCZp-oImPWEH-8OfOEqQ5gWMPPZf9K6EBfZJXX-FBs13_B_Irn6iM1C4J5Z0NPQRDvoQ"

export const RichTextField = ({ field }: Props): JSX.Element => {
    const { ClickHereToEdit } = Labels
    const dispatch = useAppDispatch()

    const { isLoading: isFieldLoading, currentFieldInEditMode = "" } =
        useAppSelector((state: GlobalState) => state.fields)
    const { loadingResources } = useAppSelector(
        (state: GlobalState) => state.resources,
    )

    const language = useAppSelector(
        (state: GlobalState) =>
            state.userFormattingSettings.language.slice(0, 2) ?? "en",
    )

    const {
        odataType: _odataType,
        value = "",
        description,
        itemId,
        maxLength,
        position: _position,
        ...fieldProps
    } = field

    const handleOnSave = useCallback(
        async (newValue: string): Promise<void> => {
            if (newValue === value) {
                return
            }
            await dispatch(
                updateField({
                    field,
                    newValue,
                }),
            ).unwrap()
        },
        [value, dispatch, field, updateField],
    )

    const isLoading =
        fieldProps.isLoading || isFieldLoading || loadingResources.length > 0

    const isDisabled =
        fieldProps.isDisabled ||
        !["", fieldProps.fieldId].includes(currentFieldInEditMode)

    const headerHeight =
        document.getElementById("newui-resource-header")?.offsetHeight ?? 0

    return (
        <RTF
            {...fieldProps}
            language={language}
            licenseKey={__DEBUG__ ? CKEDITOR_DEV_KEY : CKEDITOR_PROD_KEY}
            isLoading={isLoading}
            onSave={handleOnSave}
            extraClasses="tw-w-full"
            placeholder={fieldProps.isReadOnly ? "" : ClickHereToEdit}
            value={value}
            isImageUploadEnabled={true}
            maxLength={maxLength}
            isDisabled={isDisabled}
            viewportOffset={{
                top: headerHeight,
            }}
            fileUpload={{
                url: getImageConnectorUrl(env.applicationPath, itemId),
                withCredentials: true,
                headers: {},
            }}
            description={
                <HTMLAndInternalLinkParser
                    content={(field?.description as string) ?? ""}
                />
            }
            formatValue={(value: string): JSX.Element => {
                return (
                    <HTMLAndInternalLinkParser
                        content={value}
                        parseHTML={true}
                    />
                )
            }}
            onFieldEnterEditMode={(fieldId): void => {
                dispatch(
                    setCurrentFieldInEditMode(fieldId ?? fieldProps.fieldId),
                )
            }}
            onFieldExitEditMode={(): void => {
                dispatch(setCurrentFieldInEditMode(""))
            }}
        />
    )
}

export default RichTextField
